import React from 'react';

interface sessionStorageController<TValue> {
    get: () => TValue | null;
    set: (value: TValue) => void;
  }

const createBooleanAccessors = (key: string): sessionStorageController<boolean> => ({
    get: () => {
        const value = window.sessionStorage.getItem(key); 
        if (value === null) {
            return null;
        }
        else {
            return value === "1";
        }
    },
    set: (value: boolean) => window.sessionStorage.setItem(key, value ? "1" : "0"),
  });

// 
export const useSessionBooleanState = (key: string, defaultValue: boolean): [boolean, (value: boolean) => void] => {
    /**
     * Similar to useState<bool>(default) but persists the state to window.sessionStorage
     */

    // Setup access to window.sessionStorage for the given key
    const { get, set } = createBooleanAccessors(key);

    const getInitialState = (): boolean => {
        /**
         * Retrieves the value from window.sessionStorage, defaulting to defalueValue.
         */
        const stored = get();
        if (stored !== null) 
            return stored;
        return defaultValue;
    }

    // Wrap the sessionStorage with regular React state.
    const [state, setState] = React.useState<boolean>(getInitialState);

    // Keep the state up-to-date if the key changes.
    React.useEffect(() => {
        const newState = getInitialState();
        if (newState !== state) 
            setState(newState);
    }, [key]);

    return [
      state,
      (value: boolean) => {
        setState(value);
        set(value);
      },
    ];
  };
